import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { farmAPI } from '../services/api';

const FarmManagementPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // State management
  const [searchSBI, setSearchSBI] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [allFarms, setAllFarms] = useState([]);
  const [managedFarms, setManagedFarms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [activeTab, setActiveTab] = useState('search'); // 'search', 'all', 'managed'
  
  // Create farm form state
  const [createFormData, setCreateFormData] = useState({
    name: '',
    sbi: '',
    farm_type: '',
    total_area_hectares: '',
    address: '',
    postcode: '',
    contact_email: '',
    contact_phone: ''
  });
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Check if user is an agent or admin
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    // For now, we'll assume agents have access - later you can add role checking
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Load all farms and managed farms in parallel
      const [allFarmsResponse, managedFarmsResponse] = await Promise.all([
        farmAPI.getAllFarms(),
        farmAPI.getMyFarms().catch(() => ({ farms: [], count: 0 })) // Fallback if not an agent
      ]);
      
      setAllFarms(allFarmsResponse.farms || []);
      setManagedFarms(managedFarmsResponse.farms || []);
    } catch (error) {
      console.error('Error loading farm data:', error);
      setError('Failed to load farm data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSBISearch = async (e) => {
    e.preventDefault();
    if (!searchSBI.trim()) {
      setError('Please enter an SBI number');
      return;
    }

    setIsSearching(true);
    setError('');
    setSearchResult(null);

    try {
      const result = await farmAPI.searchBySBI(searchSBI.trim());
      setSearchResult(result);
      
      if (!result.found) {
        // Prepare create form with SBI pre-filled
        setCreateFormData(prev => ({ ...prev, sbi: searchSBI.trim() }));
      }
    } catch (error) {
      console.error('Error searching farm:', error);
      if (error.response?.status === 404) {
        setSearchResult({
          found: false,
          message: `No farm found with SBI: ${searchSBI}`,
          suggestion: {
            action: 'create_new',
            message: 'Would you like to create a new farm with this SBI?'
          }
        });
        setCreateFormData(prev => ({ ...prev, sbi: searchSBI.trim() }));
      } else {
        setError('Error searching for farm. Please try again.');
      }
    } finally {
      setIsSearching(false);
    }
  };

  const handleAllFarmsSearch = async (query) => {
    try {
      const response = await farmAPI.getAllFarms(query);
      setAllFarms(response.farms || []);
    } catch (error) {
      console.error('Error searching farms:', error);
      setError('Error searching farms. Please try again.');
    }
  };

  const handleCreateFarm = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    setError('');
    setSuccess('');

    try {
      const newFarm = await farmAPI.createFarm(createFormData);
      setSuccess(`Farm "${newFarm.name}" created successfully!`);
      
      // Reset form and refresh data
      setCreateFormData({
        name: '',
        sbi: '',
        farm_type: '',
        total_area_hectares: '',
        address: '',
        postcode: '',
        contact_email: '',
        contact_phone: ''
      });
      setShowCreateForm(false);
      setSearchResult(null);
      setSearchSBI('');
      
      // Refresh data
      await loadData();
      
      // Switch to all farms tab to show the new farm
      setActiveTab('all');
    } catch (error) {
      console.error('Error creating farm:', error);
      setError(error.response?.data?.error || 'Error creating farm. Please check all fields and try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleFormChange = (field, value) => {
    setCreateFormData(prev => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading farm data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white shadow rounded-lg p-6 mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Farm Management</h1>
              <p className="mt-2 text-gray-600">Search, manage, and create farm records</p>
            </div>
            <Link 
              to="/profile"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Back to Profile
            </Link>
          </div>
        </div>

        {/* Alert Messages */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-800">{success}</p>
              </div>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('search')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'search'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Search by SBI
              </button>
              <button
                onClick={() => setActiveTab('all')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'all'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                All Farms ({allFarms.length})
              </button>
              <button
                onClick={() => setActiveTab('managed')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'managed'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                My Managed Farms ({managedFarms.length})
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Search by SBI Tab */}
            {activeTab === 'search' && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Search Farm by SBI</h2>
                
                <form onSubmit={handleSBISearch} className="mb-6">
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <label htmlFor="sbi" className="block text-sm font-medium text-gray-700 mb-2">
                        SBI Number
                      </label>
                      <input
                        type="text"
                        id="sbi"
                        value={searchSBI}
                        onChange={(e) => setSearchSBI(e.target.value)}
                        placeholder="Enter SBI number (e.g., 123456789)"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="submit"
                        disabled={isSearching}
                        className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                      >
                        {isSearching ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Searching...
                          </>
                        ) : (
                          'Search'
                        )}
                      </button>
                    </div>
                  </div>
                </form>

                {/* Search Results */}
                {searchResult && (
                  <div className="mt-6">
                    {searchResult.found ? (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-green-800 mb-4">Farm Found!</h3>
                        <FarmCard farm={searchResult.farm} showActions={true} />
                      </div>
                    ) : (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-yellow-800 mb-2">Farm Not Found</h3>
                        <p className="text-yellow-700 mb-4">{searchResult.message}</p>
                        <p className="text-yellow-700 mb-4">{searchResult.suggestion?.message}</p>
                        <button
                          onClick={() => setShowCreateForm(true)}
                          className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
                        >
                          Create New Farm
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* All Farms Tab */}
            {activeTab === 'all' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">All Registered Farms</h2>
                  <button
                    onClick={() => setShowCreateForm(true)}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Create New Farm
                  </button>
                </div>

                {/* Search All Farms */}
                <div className="mb-6">
                  <label htmlFor="search-all" className="block text-sm font-medium text-gray-700 mb-2">
                    Search Farms
                  </label>
                  <input
                    type="text"
                    id="search-all"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      handleAllFarmsSearch(e.target.value);
                    }}
                    placeholder="Search by farm name, SBI, postcode, or address..."
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <FarmList farms={allFarms} showActions={true} />
              </div>
            )}

            {/* Managed Farms Tab */}
            {activeTab === 'managed' && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-6">My Managed Farms</h2>
                <FarmList farms={managedFarms} showActions={true} />
              </div>
            )}
          </div>
        </div>

        {/* Create Farm Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
              <CreateFarmForm
                formData={createFormData}
                onChange={handleFormChange}
                onSubmit={handleCreateFarm}
                onCancel={() => {
                  setShowCreateForm(false);
                  setCreateFormData({
                    name: '',
                    sbi: '',
                    farm_type: '',
                    total_area_hectares: '',
                    address: '',
                    postcode: '',
                    contact_email: '',
                    contact_phone: ''
                  });
                }}
                isCreating={isCreating}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Farm Card Component
const FarmCard = ({ farm, showActions = false }) => {
  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">{farm.name}</h3>
          <p className="text-sm text-gray-600 mb-2">SBI: {farm.sbi}</p>
          
          <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
            <div>
              <p><span className="font-medium">Type:</span> {farm.farm_type || 'Not specified'}</p>
              <p><span className="font-medium">Size:</span> {farm.total_area_hectares ? `${farm.total_area_hectares} hectares` : 'Not specified'}</p>
              <p><span className="font-medium">Address:</span> {farm.address || 'Not specified'}</p>
            </div>
            <div>
              <p><span className="font-medium">Postcode:</span> {farm.postcode || 'Not specified'}</p>
              <p><span className="font-medium">Email:</span> {farm.contact_email || 'Not specified'}</p>
              <p><span className="font-medium">Phone:</span> {farm.contact_phone || 'Not specified'}</p>
            </div>
          </div>
        </div>
        
        {showActions && (
          <div className="ml-4 flex flex-col space-y-2">
            <button className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">
              View Details
            </button>
            <button className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700">
              Edit
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Farm List Component
const FarmList = ({ farms, showActions = false }) => {
  if (farms.length === 0) {
    return (
      <div className="text-center py-8">
        <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
          <path d="M34 40h10v-4a6 6 0 00-10.712-3.714M34 40H14m20 0v-4a9.971 9.971 0 00-.712-3.714M14 40H4v-4a6 6 0 0110.713-3.714M14 40v-4c0-1.313.253-2.566.713-3.714m0 0A9.971 9.971 0 0124 24c4.21 0 7.813 2.602 9.288 6.286" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">No farms found</h3>
        <p className="mt-1 text-sm text-gray-500">Get started by creating a new farm or searching with different criteria.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {farms.map((farm) => (
        <FarmCard key={farm.id} farm={farm} showActions={showActions} />
      ))}
    </div>
  );
};

// Create Farm Form Component
const CreateFarmForm = ({ formData, onChange, onSubmit, onCancel, isCreating }) => {
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Create New Farm</h3>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <form onSubmit={onSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Farm Name *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => onChange('name', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SBI Number *
            </label>
            <input
              type="text"
              required
              value={formData.sbi}
              onChange={(e) => onChange('sbi', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Farm Type
            </label>
            <select
              value={formData.farm_type}
              onChange={(e) => onChange('farm_type', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
            >
              <option value="">Select farm type</option>
              <option value="dairy">Dairy</option>
              <option value="arable">Arable</option>
              <option value="livestock">Livestock</option>
              <option value="mixed">Mixed</option>
              <option value="organic">Organic</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Size (hectares)
            </label>
            <input
              type="number"
              min="0"
              step="0.1"
              value={formData.total_area_hectares}
              onChange={(e) => onChange('total_area_hectares', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address
            </label>
            <textarea
              value={formData.address}
              onChange={(e) => onChange('address', e.target.value)}
              placeholder="Full farm address"
              rows={2}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Postcode
            </label>
            <input
              type="text"
              value={formData.postcode}
              onChange={(e) => onChange('postcode', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contact Phone
            </label>
            <input
              type="tel"
              value={formData.contact_phone}
              onChange={(e) => onChange('contact_phone', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contact Email
            </label>
            <input
              type="email"
              value={formData.contact_email}
              onChange={(e) => onChange('contact_email', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
            />
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isCreating}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isCreating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creating...
              </>
            ) : (
              'Create Farm'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FarmManagementPage;